import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ImageUpload } from "@/components/ImageUpload";
import { Thumb, DateRange, ArchivedToggle, Row } from "@/components/list";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Loader2, RefreshCw, Copy, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const BatchBuilder = () => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [creatives, setCreatives] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [batchName, setBatchName] = useState("");
  const [sending, setSending] = useState(false);
  const [archived, setArchived] = useState(false);
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    if (!clientId) { setCreatives([]); setBatches([]); return; }
    const bp = { client_id: clientId, archived };
    if (from) bp.date_from = from; if (to) bp.date_to = to;
    const [cr, b] = await Promise.all([
      api.get("/creatives", { params: { client_id: clientId } }),
      api.get("/batches", { params: bp }),
    ]);
    setCreatives(cr.data); setBatches(b.data);
  }, [clientId, archived, from, to]);
  useEffect(() => { load(); setSelected({}); }, [load]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const chosen = Object.keys(selected).filter((k) => selected[k]);

  const sendBatch = async () => {
    if (chosen.length === 0) { toast.error("Select at least one creative"); return; }
    setSending(true);
    try { await api.post("/batches/send", { client_id: clientId, creative_ids: chosen, batch_name: batchName });
      toast.success("Batch sent — client notified"); setSelected({}); setBatchName(""); load();
    } catch (e) { toast.error("Could not send batch"); }
    setSending(false);
  };

  const shareUrl = (b) => `${window.location.origin}/b/${b.share_token}`;
  const copyLink = async (b) => { try { await navigator.clipboard.writeText(shareUrl(b)); toast.success("Link copied"); } catch (e) { window.prompt("Copy this link:", shareUrl(b)); } };
  const regenerate = async (b) => { await api.post(`/batches/${b.id}/regenerate`); toast.success("New link generated"); load(); };
  const toggleEnabled = async (b, v) => { await api.put(`/batches/${b.id}`, { enabled: v }); toast.success(v ? "Enabled" : "Disabled"); load(); };
  const setThumb = async (b, p) => { await api.put(`/batches/${b.id}`, { thumbnail_path: p }); toast.success("Cover updated"); load(); };
  const setArch = async (b, v) => { await api.put(`/batches/${b.id}`, { archived: v }); toast.success(v ? "Archived" : "Unarchived"); load(); };

  const eligible = creatives.filter((c) => !c.sent_to_client);

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Creative Batches</h1>
        <p className="text-zinc-400 mt-1">Curate creatives, send to a client, and manage share links.</p></div>

      <div className="flex flex-wrap gap-3 items-end">
        <div><label className="text-zinc-400 text-sm">Client</label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="batch-client-select" className="w-[240px] bg-ink-800 border-ink-700 mt-1.5"><SelectValue placeholder="Select a client" /></SelectTrigger>
            <SelectContent className="bg-ink-800 border-ink-700 text-white">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-zinc-400 text-sm">Batch name</label><Input data-testid="batch-name-input" value={batchName} onChange={(e) => setBatchName(e.target.value)} placeholder="June Batch A" className="w-[200px] bg-ink-800 border-ink-700 mt-1.5" /></div>
        <Button data-testid="send-batch-button" onClick={sendBatch} disabled={sending || !clientId} className="h-11 px-6 rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold">{sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send ({chosen.length})</Button>
      </div>

      {!clientId && <p className="text-zinc-500">Pick a client to begin.</p>}

      {clientId && (
        <>
          <h2 className="font-display text-xl font-bold">Unsent creatives</h2>
          <div className="space-y-2">
            {eligible.map((c) => (
              <div key={c.id} role="button" tabIndex={0} data-testid={`batch-card-${c.id}`} onClick={() => toggle(c.id)}
                className={`flex items-center gap-3 rounded-xl p-2.5 border cursor-pointer transition-all ${selected[c.id] ? "border-brand ring-1 ring-brand/40 bg-ink-800" : "border-ink-700 bg-ink-800 hover:border-ink-600"}`}>
                <Checkbox checked={!!selected[c.id]} className="border-zinc-500 data-[state=checked]:bg-brand data-[state=checked]:text-ink-900" />
                <Thumb src={(c.image_path || c.client_thumbnail) ? fileUrl(c.image_path || c.client_thumbnail) : null} alt={c.title} />
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{c.title}</p><p className="text-zinc-500 text-xs">{c.type}</p></div>
                <StatusBadge status={c.status} />
              </div>
            ))}
            {eligible.length === 0 && <p className="text-zinc-500">No unsent creatives.</p>}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <h2 className="font-display text-xl font-bold">Shared batches</h2>
            <div className="flex items-center gap-3"><DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} /><ArchivedToggle archived={archived} onToggle={() => setArchived((a) => !a)} /></div>
          </div>
          <div className="space-y-3">
            {batches.map((b) => (
              <div key={b.id} data-testid={`batch-share-${b.id}`} className="bg-ink-800 border border-ink-700 rounded-xl p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <Thumb src={b.thumbnail_path ? fileUrl(b.thumbnail_path) : null} alt={b.batch_name} />
                  <div className="flex-1 min-w-0"><p className="font-semibold truncate">{b.batch_name}</p><p className="text-zinc-500 text-xs">{b.count} sent</p></div>
                  <span className="text-xs text-zinc-400">{b.enabled ? "Active" : "Off"}</span>
                  <Switch data-testid={`batch-enabled-${b.id}`} checked={b.enabled} onCheckedChange={(v) => toggleEnabled(b, v)} />
                  <button data-testid={`archive-batch-${b.id}`} onClick={() => setArch(b, !archived)} className="text-zinc-500 hover:text-brand" title={archived ? "Unarchive" : "Archive"}>{archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</button>
                </div>
                <div className="flex gap-2">
                  <Input readOnly value={shareUrl(b)} data-testid={`batch-link-${b.id}`} className="bg-ink-900 border-ink-700 h-9 text-xs text-zinc-400" />
                  <Button data-testid={`copy-link-${b.id}`} onClick={() => copyLink(b)} size="sm" className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
                  <Button data-testid={`regenerate-link-${b.id}`} onClick={() => regenerate(b)} size="sm" variant="outline" className="rounded-full border-ink-700 bg-ink-900 hover:bg-ink-700 shrink-0"><RefreshCw className="w-4 h-4" /></Button>
                </div>
                <ImageUpload label="Batch cover (overrides client default)" value={b.thumbnail_path} onChange={(p) => setThumb(b, p)} testid={`batch-thumb-${b.id}`} />
              </div>
            ))}
            {batches.length === 0 && <p className="text-zinc-500">No {archived ? "archived" : "shared"} batches.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default BatchBuilder;
